import { useState, useMemo } from 'react';
import { ProjectCard, Project } from './ProjectCard';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  Plus, 
  Search,
  Briefcase,
  Users,
  TrendingUp,
  CheckCircle,
  AlertCircle,
  Settings,
  Download,
  Building,
  Target,
  Grid,
  List
} from 'lucide-react';

interface ProjectsDashboardProps {
  projects: Project[];
  onCreateProject: () => void;
  onViewProject: (project: Project) => void;
  onEditProject: (project: Project) => void;
  onDeleteProject: (projectId: string) => void;
  onDuplicateProject: (project: Project) => void;
}

export function ProjectsDashboard({ 
  projects, 
  onCreateProject, 
  onViewProject, 
  onEditProject, 
  onDeleteProject,
  onDuplicateProject 
}: ProjectsDashboardProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('deadline');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = projects.filter(project => {
      const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           project.location.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || project.priority === priorityFilter;
      
      return matchesSearch && matchesStatus && matchesPriority;
    });

    // Sort projects
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'deadline':
          return a.deadline.getTime() - b.deadline.getTime();
        case 'created':
          return b.createdAt.getTime() - a.createdAt.getTime();
        case 'priority':
          const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
          return priorityOrder[b.priority] - priorityOrder[a.priority];
        case 'applicants':
          return b.progress.applicants - a.progress.applicants;
        case 'title':
          return a.title.localeCompare(b.title);
        case 'company':
          return a.company.localeCompare(b.company);
        default:
          return 0;
      }
    });

    return filtered;
  }, [projects, searchTerm, statusFilter, priorityFilter, sortBy]);

  // Calculate dashboard stats
  const stats = useMemo(() => {
    const total = projects.length;
    const active = projects.filter(p => p.status === 'active').length;
    const urgent = projects.filter(p => p.priority === 'urgent').length;
    const totalApplicants = projects.reduce((sum, p) => sum + p.progress.applicants, 0);
    const totalHires = projects.reduce((sum, p) => sum + p.progress.hires, 0);
    const avgQualityScore = projects.length > 0 
      ? projects.reduce((sum, p) => sum + p.analytics.qualityScore, 0) / projects.length 
      : 0;
    
    // Calculate urgent projects (deadline within 7 days)
    const urgentDeadlines = projects.filter(p => {
      const daysUntilDeadline = Math.ceil((p.deadline.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return daysUntilDeadline <= 7 && daysUntilDeadline >= 0;
    }).length;
    
    return { 
      total, 
      active, 
      urgent, 
      totalApplicants, 
      totalHires, 
      avgQualityScore: Math.round(avgQualityScore * 10) / 10,
      urgentDeadlines 
    };
  }, [projects]);

  const handleArchiveProject = (project: Project) => {
    if (window.confirm(`Are you sure you want to archive "${project.title}"?`)) {
      onDeleteProject(project.id);
    }
  };

  const exportProjects = () => {
    const dataStr = JSON.stringify(projects, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    const exportFileDefaultName = `hiring-projects-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="space-y-6">
        <div className="text-center space-y-4">
          <div className="flex items-center justify-center gap-3">
            <div className="h-12 w-12 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/20">
              <Briefcase className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Hiring Projects</h1>
              <p className="text-slate-600">
                {projects.length === 0 
                  ? "Create and manage your hiring initiatives" 
                  : `Managing ${stats.total} project${stats.total !== 1 ? 's' : ''} across your organization`
                }
              </p>
            </div>
          </div>
        </div>
        
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex gap-3">
            {projects.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-lg border-slate-200 hover:bg-slate-50">
                    <Settings className="h-4 w-4 mr-2" />
                    Actions
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  <DropdownMenuItem onClick={exportProjects} className="rounded-md hover:bg-slate-50">
                    <Download className="h-4 w-4 mr-2" />
                    Export Projects
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <Button 
            onClick={onCreateProject}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 hover:from-slate-800 hover:to-slate-700 text-white rounded-lg shadow-lg shadow-slate-900/20 hover:shadow-xl hover:shadow-slate-900/30 transition-all duration-200"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg flex-shrink-0">
                <Building className="h-5 w-5 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                <div className="text-xs text-slate-600 font-medium">Total Projects</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-green-100 rounded-lg flex-shrink-0">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.active}</div>
                <div className="text-xs text-slate-600 font-medium">Active Projects</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-100 rounded-lg flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.urgentDeadlines}</div>
                <div className="text-xs text-slate-600 font-medium">Urgent Deadlines</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg flex-shrink-0">
                <Users className="h-5 w-5 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.totalApplicants}</div>
                <div className="text-xs text-slate-600 font-medium">Total Applicants</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg flex-shrink-0">
                <Target className="h-5 w-5 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.totalHires}</div>
                <div className="text-xs text-slate-600 font-medium">Successful Hires</div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl shadow-sm border border-slate-200 hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-100 rounded-lg flex-shrink-0">
                <TrendingUp className="h-5 w-5 text-slate-600" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-2xl font-bold text-slate-900">{stats.avgQualityScore}</div>
                <div className="text-xs text-slate-600 font-medium">Avg Quality</div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      {projects.length > 0 && (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardContent className="p-6">
            <div className="grid gap-4 md:grid-cols-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
                <Input
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400"
                />
              </div>

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  <SelectItem value="all" className="rounded-md hover:bg-slate-50">All Status</SelectItem>
                  <SelectItem value="active" className="rounded-md hover:bg-slate-50">Active</SelectItem>
                  <SelectItem value="paused" className="rounded-md hover:bg-slate-50">Paused</SelectItem>
                  <SelectItem value="completed" className="rounded-md hover:bg-slate-50">Completed</SelectItem>
                  <SelectItem value="draft" className="rounded-md hover:bg-slate-50">Draft</SelectItem>
                </SelectContent>
              </Select>

              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400">
                  <SelectValue placeholder="All Priority" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  <SelectItem value="all" className="rounded-md hover:bg-slate-50">All Priority</SelectItem>
                  <SelectItem value="urgent" className="rounded-md hover:bg-slate-50">Urgent</SelectItem>
                  <SelectItem value="high" className="rounded-md hover:bg-slate-50">High</SelectItem>
                  <SelectItem value="medium" className="rounded-md hover:bg-slate-50">Medium</SelectItem>
                  <SelectItem value="low" className="rounded-md hover:bg-slate-50">Low</SelectItem>
                </SelectContent>
              </Select>

              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="rounded-lg border-slate-200 focus:border-slate-400 focus:ring-slate-400">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent className="rounded-lg border border-slate-200 bg-white shadow-lg">
                  <SelectItem value="deadline" className="rounded-md hover:bg-slate-50">Deadline</SelectItem>
                  <SelectItem value="created" className="rounded-md hover:bg-slate-50">Date Created</SelectItem>
                  <SelectItem value="priority" className="rounded-md hover:bg-slate-50">Priority</SelectItem>
                  <SelectItem value="applicants" className="rounded-md hover:bg-slate-50">Applicants</SelectItem>
                  <SelectItem value="title" className="rounded-md hover:bg-slate-50">Title</SelectItem>
                  <SelectItem value="company" className="rounded-md hover:bg-slate-50">Company</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                  className={viewMode === 'grid' 
                    ? 'bg-slate-900 hover:bg-slate-800 text-white' 
                    : 'border-slate-200 hover:bg-slate-50'
                  }
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className={viewMode === 'list' 
                    ? 'bg-slate-900 hover:bg-slate-800 text-white' 
                    : 'border-slate-200 hover:bg-slate-50'
                  }
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>

              <div className="text-sm text-slate-600 font-medium flex items-center">
                {filteredProjects.length} of {projects.length} projects
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Projects Grid/List */}
      {filteredProjects.length === 0 ? (
        <Card className="bg-white rounded-xl shadow-sm border border-slate-200">
          <CardContent className="py-16 text-center">
            {projects.length === 0 ? (
              <div className="flex flex-col items-center space-y-4">
                <Briefcase className="h-16 w-16 text-slate-300" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">No Projects Yet</h3>
                  <p className="text-slate-600">Create your first hiring project to get started</p>
                </div>
                <Button onClick={onCreateProject} className="bg-slate-900 hover:bg-slate-800 text-white">
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-4">
                <Search className="h-16 w-16 text-slate-300" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold text-slate-900">No Matching Projects</h3>
                  <p className="text-slate-600">Try adjusting your search or filters</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className={viewMode === 'grid' 
          ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6' 
          : 'space-y-4'
        }>
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              viewMode={viewMode}
              onView={() => onViewProject(project)}
              onEdit={() => onEditProject(project)}
              onDelete={() => handleArchiveProject(project)}
              onDuplicate={() => onDuplicateProject(project)}
            />
          ))}
        </div>
      )}
    </div>
  );
} 